import React, { useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { graphql } from 'gatsby';
import { format } from 'date-fns';
import { timeToRead, isBrowser } from '@helpers/utils';
import RemoveMd from 'remove-markdown';

import SEO from '@common/SEO';
import Main from '@common/Main';
import Section from '@common/Section';
import Container from '@common/Container';
import Row from '@common/Row';
import Heading from '@common/Heading';
import Markdown from '@common/Markdown';
import Link from '@common/Link';
import Button from '@common/Button';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';

import Socials from '@components/blog/post/Socials';
import ArticleCard from '@components/blog/post/ArticleCard';
import Breadcrumbs from '@components/blog/post/Breadcrumbs';

// Comments are removed but left in as comments if we decide to show comments again on post
// import Comment from '@components/blog/post/Comment';
// import CommentSymbol from '@images/socials/comments.svg';
// import CommentForm from '@components/blog/post/CommentForm';

const mockModal = {
  title: null,
  titleAlignment:'Center',
  titleFontSize:'36px',
  size:'md',
  style:'Default',
  body: [
    {
      eventLabel:'webinar',
      formId:'1444',
      title: 'Fill out this form to learn more!',
      __typename:'ContentfulForm'
    }
  ]
}

export const query = graphql`
  query($slug: String!, $title: String!) {
    wpPost(slug: { eq: $slug }) {
      ...Posts
    }
    relatedArticles: allWpPost(
      limit: 2
      filter: {title: {ne: $title}},
      sort: {date: DESC}
    ) {
      edges {
        node {
          author {
            node {
              name
              slug
            }
          }
          title
          link
          date
          featuredImage {
            node {
              sourceUrl
            }
          }
          categories {
            nodes {
              name
              link
            }
          }
        }
      }
    }
  }
`

const Post = ({ location, data }) => {
  const {
    databaseId,
    title,
    content,
    author,
    date,
    categories,
    commentCount,
    comments
  } = data?.wpPost;
  const breadcrumbs = [
    {
      'text': 'Home', 
      'link': '/blog' 
    }, 
    {
      'text': categories?.nodes[0]?.name, 
      'link': `/blog${categories?.nodes[0]?.link}`
    }, 
    {
      'text': title
    }
  ];
  // const getReplies = (comment) => {
  //   const replies = comments?.nodes.filter(x => x.parentId === comment.id);
  //   comment.replies = replies;
  //   replies?.forEach(reply => {
  //     getReplies(reply);
  //   })
  // }
  const ref = useRef(null);
  const [isSticky, setSticky] = useState(false);
  const [isStickToBottom, setStickToBottom] = useState(false);
  const setStickyNav = () => {
    if (ref && ref.current && ref.current.getBoundingClientRect()) {
      setSticky(ref.current.getBoundingClientRect().top <= 143 && ref.current.getBoundingClientRect().bottom >= 881);
      setStickToBottom(ref.current.getBoundingClientRect().top <=0)
    }
  };
  useEffect(() => {
    setStickyNav();
    if (isBrowser()) {
      window.addEventListener('scroll', setStickyNav);
      window.addEventListener('resize', setStickyNav);
      // Cleanup listeners
      return () => {
        window.removeEventListener('scroll', () => setStickyNav);
        window.removeEventListener('resize', () => setStickyNav);
      };
    }
  }, []);
  const isWebinar = categories.nodes.some(x => x.name === 'Webinar');
  return (
    <Main>
      <Section spacing='mt-4'>
        <Container>
          <Row>
            {/* Main Content */}
            <div className='col col-xl-8 pe-xl-4'>    
              <Breadcrumbs data={breadcrumbs}/>
              <Heading type='h1' className='fs-36 mb-3'>{title}</Heading>
              {isWebinar &&
                <div className='my-3'>
                  <Heading type='h2' className='fs-24 mb-3'>{'Watch Recording'}</Heading>
                  <p>{'To get the recording and slides sent right to your email, please fill out the form below!'}</p>
                  <Button
                    variant='primary'
                    copy='Access Recordings'
                    modal={mockModal}
                  />
                </div>
              }
              <Markdown data={content} className='wp-post'/>
              {author && 
                <Row className='d-flex my-3 mx-auto py-3 border-top border-bottom border-2 border-dark-grey-4'>
                  <div className='col-auto'>
                    <img src={author?.node.avatar.url} className='rounded-circle border border-dark-grey-4 border-2' alt='' />
                  </div>
                  <div className='col'>
                    {author?.node.description && <p>{author?.node.description}</p>}
                    <Link to={`/blog/author/${author?.node.slug}`} className='d-flex align-items-center'>
                      <p className='mb-0'>{`More by ${author?.node.firstName}`}</p>
                      <FontAwesomeIcon icon={faArrowRight} style={{marginLeft: 5}} />
                    </Link>
                  </div>
                </Row>
              }
              <div className='ps-1 mb-5'>
                {/* <div className='my-2 me-auto'>
                  {(commentCount ? commentCount : '0') + (commentCount === 1 ? ' comment' : ' comments')}
                </div> */}
                <Socials location={location} addClass={'my-2 justify-content-end'}/>
              </div>
              {/* {comments?.nodes.filter(x => x.parentId == null).map(comment => {
                getReplies(comment);
                  return (
                    <Comment data={comment} postId={databaseId}/>
                  )
              })}
              <CommentForm id={databaseId}/> */}
            </div>
            {/* Sidenav */}
            <div className='d-none d-xl-flex flex-column col-4'>
              {/* Author */}
              <Link to={`/blog/author/${author?.node.slug}`} className='d-flex text-black'>
                {author?.node.avatar && <img src={author?.node.avatar.url} alt='' style={{maxHeight: 60, maxWidth: 60}} className='rounded-circle border border-dark-grey-4 border-2 ml-2' />}
                {author?.node.name && <Heading type='h3' className='fs-16 my-auto ms-1'>{author?.node.name}</Heading>}
              </Link>
              {/* Categories */}
              {categories &&
                <Row className='mt-xl-1 mb-2'>
                  {categories.nodes.map((category, index, {length}) => {
                    return (
                      <Link key={index} className='col-auto d-xl-block cat-text d-flex' to={`/blog${category.link}`} title={`${category.name}`}>
                        {category.name}
                        {(index != length-1) && 
                          <span className='d-xl-none ms-1'>{'|'}</span>
                        }
                      </Link>
                    )
                  })}
                </Row>
              }
              {/* Date */}
              {date && <p>{format(new Date(date), 'PPP')}</p>}
              {/* Time to read */}
              {content && <p>{timeToRead(RemoveMd(content))}{' mins'}</p>}
              <Socials location={location} addClass={'mt-1 mb-3'}/>
              {/* <div className='fs-20 mt-1 mb-3'>
                <img src={CommentSymbol} alt='' className='me-1'/>
                {(commentCount ? commentCount : '0') + (commentCount === 1 ? ' comment' : ' comments')}
              </div> */}
              {/* Related Articles */}
              {data.relatedArticles &&
                <div className={clsx('d-flex flex-fill mt-3 position-relative', {'align-items-end':isStickToBottom})} ref={ref}>
                  <div className={clsx({'position-fixed':isSticky})} style={{top: 136, width: 382}}>
                    <Heading type='h3'>{'Related Articles'}</Heading>
                    {data.relatedArticles.edges.map((post, index) => (
                      <ArticleCard key={index} data={post.node}/>
                    ))}
                  </div>
                </div>
              }
            </div>
          </Row>
        </Container>
      </Section>
    </Main>
  );
};

export default Post

export const Head = ({ location, data }) => {
  const {
    title,
    excerpt,
    featuredImage
  } = data?.wpPost;
  return (
    <SEO
      index={true}
      titleTag={title}
      metaDescription={RemoveMd(excerpt)}
      metaImageUrl={featuredImage?.node?.sourceUrl}
      metaImageWidth={featuredImage?.node?.width}
      metaImageHeight={featuredImage?.node?.height}
      location={location}
    />
  )
}
